import { ComponentTypes } from "../../../config/config";
import { IPageSettings } from "../../../interfaces/IPageSettings"
import { ISiteInfo } from "../../../interfaces/ISiteInfo";
import { ITeam } from "../../../interfaces/services/ITeam";
import { getSharePointDocumentLibrarySharepointId, getSharePointSite } from "../../../services/GraphService";
import { IWorkingDirectoriesBuilder } from "../../../interfaces/IWorkingDirectoriesBuilder";
import { ILocationBuilder } from "../../../interfaces/ILocationBuilder";
import { teamDocumentLibrary } from "../../../config/constants";
import { tryDecodeUriComponent } from "../../../../../utilities/EncodeDecode";

export class SharepointWorkingDirectoriesBuilder implements IWorkingDirectoriesBuilder {
    constructor(
        private folderLocationBuilder: ILocationBuilder,
        private sites: ISiteInfo[],
        private teams: ITeam[],
        private drives: any[],
        private siteId: string,
        private driveId: string,
        private componentType: string,
        private isTeamsFolder: boolean) { }

    async build(): Promise<IPageSettings[]> {
        const site = this.sites.find(site => site.id === this.siteId) ?? await getSharePointSite(this.siteId);
        const workingDirectories = await this.buildWorkingDirectories(site);
        const initialLocation = this.folderLocationBuilder.getInitialLocation();
        const documentLibraryLevel = (this.componentType === ComponentTypes.Teams || this.isTeamsFolder)
            ? this.buildTeamsDocumentLibraryDirectory()
            : this.buildSharepointDocumentLibraryDirectory(site);

        return [...[initialLocation], ...[documentLibraryLevel], ...workingDirectories];
    }

    private async buildWorkingDirectories(site: ISiteInfo): Promise<IPageSettings[]> {
        const sharepointListId = this.driveId
            ? await getSharePointDocumentLibrarySharepointId(this.driveId)
            : null;

        return this.drives.reduce((previousDirectories: IPageSettings[], drive, index) => {
            const workingDirectory: IPageSettings = this.buildWorkingDirectory(previousDirectories, drive, site, sharepointListId, index);
            return [...previousDirectories, workingDirectory];
        }, []);
    }

    private buildWorkingDirectory(previousDirectories: IPageSettings[], item: any, site: ISiteInfo, sharepointListId: string, index: number): IPageSettings {
        const rootLevel = 0;
        const name = item?.name;
        let documentLibrary: string;
        let directory: string;
        const webUrl: string = item.listItem?.webUrl || item.webUrl;
        if (webUrl?.indexOf(".aspx") > -1 && previousDirectories?.length > 0) {
            /* 
                Certain folders/items, like Document sets, point to an aspx page i.e:
                "https://docubird.sharepoint.com/sites/docubird_demo/_layouts/15/DocSetHome.aspx?id=/sites/docubird_demo/Document%20Set%20test/DS%20test%20003"
            */
            const parentDirectory: IPageSettings = previousDirectories[previousDirectories.length - 1];
            documentLibrary = parentDirectory.DocumentLibrary;
            directory = `${parentDirectory.directory}/${name}`;
        }
        else {
            directory = webUrl?.replace(`${site.webUrl}/`, "");
            documentLibrary = tryDecodeUriComponent(directory?.split("/")[0]);
        }

        return {
            id: index === rootLevel ? this.driveId : item.id,
            parentId: index === rootLevel ? null : this.driveId,
            type: ComponentTypes.SharePointFolders,
            siteId: this?.siteId,
            siteDisplayName: site?.name,
            webUrl: site?.webUrl,
            fileTitle: name,
            directory: directory,
            DocumentLibrary: documentLibrary === teamDocumentLibrary.defaultDirectory
                ? teamDocumentLibrary.defaultName
                : documentLibrary,
            displayName: name,
            isTeamsFolder: this.isTeamsFolder,
            sharePointListId: sharepointListId,
            contentTypeId: item.listItem?.contentType?.id,
            serverRelativePath: webUrl && new URL(webUrl).pathname,
            sharePointUniqueId: index !== rootLevel ? item.sharepointIds?.listItemUniqueId : undefined
        }
    }

    private buildSharepointDocumentLibraryDirectory(site: ISiteInfo): IPageSettings {
        return {
            id: this.siteId,
            type: ComponentTypes.SharePointDocuments,
            siteId: this.siteId,
            siteDisplayName: site.name,
            webUrl: site.webUrl,
            directory: "",
            DocumentLibrary: "",
            displayName: site.name,
        }
    }

    private buildTeamsDocumentLibraryDirectory(): IPageSettings {
        const team = this.teams.find(team => team.siteId === this.siteId);
        return {
            id: team.id,
            type: ComponentTypes.SharePointDocuments,
            siteId: this.siteId,
            siteDisplayName: team.displayName,
            webUrl: team.webUrl,
            directory: "",
            DocumentLibrary: "",
            teamGroupId: team.id,
            displayName: team.displayName,
            isTeamsFolder: true,
            isSharedTeam: team.isSharedTeam
        }
    }
}